import { format, parse } from 'date-fns';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { v4 } from 'uuid';
import Config from '../Config';
import LanguageStore, { languageSlug } from '../stores/LanguageStore';
import SearchStore, { NEWS_CATEGORY, NEWS_CATEGORY_ARCHIVED } from '../stores/SearchStore';
import { AnalyticsTrigger, Anchor, Text, Heading, Separator, Spinner, Tag, List, ListItem } from 'telia-front-react';
import { TranslationsContext } from '@teliaee/telia-translate';
import { FetchStatus } from '../util/FetchStatus';
import SearchErrorNotice from './SearchErrorNotice';
import { AllAndArchivedFilterOrder, NewsSortOrder, SearchEvent, SearchNewsResult } from './SearchInterfaces';
import SearchNotFoundNotice from './SearchNotFoundNotice';
import ShowMoreButton from './ShowMoreButton';
import SwitchArchiveFilterButton from './SwitchArchiveFilterButton';

interface Props {
  languageStore?: LanguageStore;
  searchStore?: SearchStore;
}

@observer
class NewsSearchResults extends React.Component<Props> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  private getNewsURL = (newsLink: string) => {
    return `${languageSlug(this.props.languageStore.currentLanguage)}/uudised/${newsLink}`;
  };

  public render(): JSX.Element {
    const status = this.props.searchStore.searchWrappers[NEWS_CATEGORY].status;

    if (status === FetchStatus.UNINITIALIZED) {
      return null;
    }
    return <>{this.getNewsBlock()}</>;
  }

  private getNewsBlock = (): JSX.Element => {
    const translate = this.context.translate;
    const {
      searchStore: { searchWrappers }
    } = this.props;

    const newsCategory = searchWrappers[NEWS_CATEGORY].filterOrder === AllAndArchivedFilterOrder.ARCHIVED ? NEWS_CATEGORY_ARCHIVED : NEWS_CATEGORY;

    const { status, results } = searchWrappers[newsCategory];

    if (status === FetchStatus.ERROR) {
      return <SearchErrorNotice showNoticeWhen={['ERROR']} status={status} translate={translate} />;
    }
    if (status === FetchStatus.FETCHING) {
      return <Spinner />;
    }
    if (!results.length) {
      return <SearchNotFoundNotice customType={newsCategory} />;
    }

    return (
      <>
        {this.displayResults(newsCategory)}
        <Separator spacing="small" weight="thin" />
        <SearchErrorNotice showNoticeWhen={['FETCH_MORE_ERROR']} status={status} translate={translate} />
        <ShowMoreButton category={newsCategory} />
        <SwitchArchiveFilterButton category={NEWS_CATEGORY} labelPrefix="news" />
      </>
    );
  };

  private displayResults = (category: string): JSX.Element => {
    const translate = this.context.translate;
    const results = this.props.searchStore.searchWrappers[category].results;
    return (
      <Text>
        {results.map((result: SearchNewsResult) => (
          <React.Fragment key={result.objectID}>
            <Separator spacing="small" weight="thin" />
            <article>
              <List type="unstyled" direction="inline" bordered>
                <ListItem className="text-color-normal text-tiny">
                  <time className="text-small">{format(parse(result.date, "yyyy-MM-dd'T'HH:mm:ss", Date.now()), 'dd.MM.yyyy HH:mm')}</time>
                </ListItem>
                {category === NEWS_CATEGORY_ARCHIVED && (
                  <ListItem className="text-color-normal text-tiny">
                    <Tag key="archived_tag" text={translate('newsSearchResults.tagArchived')} purpose="text" size={'tiny'} />
                  </ListItem>
                )}
              </List>
              <AnalyticsTrigger trigger={{ id: `Tab - ${category} - Link - ${result.title}`, callback: 'onClick' }} key={result.objectID}>
                <Anchor
                  onClick={() => this.sendAlgoliaAnalyticsData(result, category === NEWS_CATEGORY_ARCHIVED)}
                  url={this.getNewsURL(result.link)}
                  isTextColor
                  className="text-weight-normal"
                >
                  <Heading element="h4">{result.title}</Heading>
                </Anchor>
              </AnalyticsTrigger>
              <Separator color="invisible" spacing="xxsmall" />
              <div dangerouslySetInnerHTML={{ __html: result.content }} />
            </article>
          </React.Fragment>
        ))}
      </Text>
    );
  };

  private sendAlgoliaAnalyticsData = async (news: SearchNewsResult, archived: boolean) => {
    const { searchStore } = this.props;
    const category = archived ? NEWS_CATEGORY_ARCHIVED : NEWS_CATEGORY;
    const sortOrder = searchStore.searchWrappers[category].sortOrder.toLowerCase();
    const idxName = `${Config.componentTier}_news${archived ? '-archive' : ''}${sortOrder === NewsSortOrder.WEIGHT ? '' : `_${sortOrder}_desc`}`;
    const searchEvent: SearchEvent = {
      eventName: 'News search result click',
      eventType: 'click',
      index: idxName,
      objectIDs: [news.objectID],
      userToken: v4(),
      queryID: searchStore.searchWrappers[category].queryId,
      positions: [searchStore.searchWrappers[category].results.findIndex((p) => p.objectID === news.objectID) + 1]
    };

    searchStore.postSearchTabAnalytics(NEWS_CATEGORY);
    searchStore.postSearchResultClickEvent(searchEvent);
  };
}
export default inject('languageStore', 'searchStore')(NewsSearchResults);
